import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { approvalsService } from '@/services/demandPlanning/approvals.service';
import { consensusOverviewQueryKeys } from '../hooks/useConsensusOverviewQueries';

interface Props {
  planningCycleId: number | null;
}

export function PendingApprovalsPanel({ planningCycleId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const approvals = useQuery({
    queryKey: ['approvals-pending', planningCycleId],
    queryFn: () => approvalsService.listForCycle(planningCycleId!, 'pending'),
    enabled: planningCycleId != null,
  });

  const decide = useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: number;
      status: 'approved' | 'rejected';
    }) => approvalsService.decide(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approvals-pending'] });
      void queryClient.invalidateQueries({ queryKey: consensusOverviewQueryKeys.metaPrefix });
      void queryClient.invalidateQueries({ queryKey: consensusOverviewQueryKeys.allPrefix });
    },
  });

  const rows = approvals.data ?? [];
  if (planningCycleId == null || (!approvals.isLoading && rows.length === 0)) {
    return null;
  }

  return (
    <section className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
      <h2 className="text-sm font-semibold text-gray-900">{t('approvals.pendingTitle')}</h2>
      <p className="mt-0.5 text-xs text-gray-600">{t('approvals.pendingHint')}</p>
      {approvals.isLoading ? (
        <p className="mt-3 text-sm text-gray-500">{t('common.loading')}</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-100 bg-white px-3 py-2 text-sm"
            >
              <div>
                <span className="font-medium text-gray-800">{row.approval_role}</span>
                <span className="mx-2 text-gray-400">·</span>
                <span className="text-gray-600">
                  {row.entity_type} #{row.entity_id}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="rounded-md border border-green-300 px-2 py-1 text-xs text-green-800 hover:bg-green-50 disabled:opacity-50"
                  disabled={decide.isPending}
                  onClick={() => void decide.mutate({ id: row.id, status: 'approved' })}
                >
                  {t('approvals.approve')}
                </button>
                <button
                  type="button"
                  className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-800 hover:bg-red-50 disabled:opacity-50"
                  disabled={decide.isPending}
                  onClick={() => void decide.mutate({ id: row.id, status: 'rejected' })}
                >
                  {t('approvals.reject')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
